import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const CookiesPage = () => {
  const lastUpdated = "March 6, 2026";

  return (
    <div className="min-h-screen bg-[#050505] pt-20">
      {/* Decorative orange orbs */}
      <div className="orange-orb orange-orb-1 top-32 -left-32 w-[28rem] h-[28rem] z-0" />
      <div className="orange-orb orange-orb-2 top-1/2 -right-32 w-[32rem] h-[32rem] z-0" />
      <div className="relative z-10">
      <SEO
        title="Cookies Policy"
        description="Agentic Atoms cookies policy. Learn how we use cookies and similar technologies on our website."
        path="/cookies"
      />
      {/* Back button */}
      <div className="max-w-4xl mx-auto px-6 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-6 pb-20">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-[#FF6B00]/10 border border-[#FF6B00]/25 rounded-2xl flex items-center justify-center">
              <Cookie className="w-7 h-7 text-[#FF6B00]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Cookies Policy</h1>
              <p className="text-sm text-[#71717A]">Last updated: {lastUpdated}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-gray max-w-none"
        >
          <div className="space-y-8 text-[#A1A1AA] leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. What Are Cookies?</h2>
              <p>
                Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences, understand how you use the site, and improve your overall experience.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Cookies</h2>
              <p className="mb-3">Agentic Atoms uses cookies for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Essential Cookies:</strong> These are necessary for the website to function properly and cannot be disabled.</li>
                <li><strong>Functionality Cookies:</strong> These remember your preferences, such as your cookie consent choice.</li>
                <li><strong>Analytics Cookies:</strong> These help us understand how visitors interact with our website so we can improve it.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Types of Cookies We Use</h2>
              
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full bg-[#050505] border border-white/10 rounded-xl overflow-hidden">
                  <thead className="bg-white/[0.03]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-white">Cookie Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-white">Purpose</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    <tr>
                      <td className="px-4 py-3 text-sm">cookie_consent</td>
                      <td className="px-4 py-3 text-sm">Stores your cookie consent preference</td>
                      <td className="px-4 py-3 text-sm">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">session_id</td>
                      <td className="px-4 py-3 text-sm">Maintains your chatbot session</td>
                      <td className="px-4 py-3 text-sm">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm">admin_auth</td>
                      <td className="px-4 py-3 text-sm">Admin authentication (if applicable)</td>
                      <td className="px-4 py-3 text-sm">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Third-Party Cookies</h2>
              <p className="mb-3">We may use third-party services that set their own cookies:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Calendly:</strong> When you use our scheduling feature, Calendly may set cookies to manage appointments.</li>
                <li><strong>Analytics Services:</strong> We may use analytics tools to understand website usage patterns.</li>
              </ul>
              <p className="mt-3">
                These third parties have their own cookie policies, and we encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Managing Cookies</h2>
              <p className="mb-3">You can control cookies through several methods:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cookie Banner:</strong> When you first visit our site, you can choose to accept or decline non-essential cookies.</li>
                <li><strong>Browser Settings:</strong> Most browsers allow you to block or delete cookies through their settings menu.</li>
                <li><strong>Opt-Out Links:</strong> Some third-party services provide opt-out mechanisms.</li>
              </ul>
              <p className="mt-3">
                Please note that blocking certain cookies may affect your experience on our website.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. How to Delete Cookies</h2>
              <p className="mb-3">You can delete cookies from your browser:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Clear browsing data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data → Clear Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Privacy, search, and services → Clear browsing data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Updates to This Policy</h2>
              <p>
                We may update this Cookies Policy from time to time to reflect changes in our practices or for legal reasons. We will post any updates on this page with a new "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Contact Us</h2>
              <p>
                If you have any questions about our use of cookies, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-white/[0.03] rounded-xl">
                <p><strong>Agentic Atoms</strong></p>
                <p>Email: connect@agenticatoms.com</p>
                <p>Phone: +1-227-241-6372</p>
                <p>Address: 5830 E 2nd St, Ste 7000 #30038, Casper, Wyoming 82609 US</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div></div>
    </div>
  );
};

export default CookiesPage;
